import { NO_FOUND_STATE_WORDS } from '@/lib/expected-noyau-words';
import {
  ANALYST_LEVELS,
  ANALYST_LEVEL_LABELS,
  type AnalystLevel,
  NOYAU_CODES,
  type NoyauCode,
  type NoyauDeterminationState,
  type NoyauExpectedProvenance,
  type NoyauPlacement,
  type NoyauScore,
  type NoyauVerdict,
  noyauOf,
  noyauPoints,
} from '@matter_extractor/shared';

/**
 * The words of the band « Face à l'attendu » of the parcours (npc.md §26.3, issue #216, lot K):
 * pure, so that the component only draws what this module says, and so that the sentences are
 * tested apart from any screen. The words shared with the list of sessions and the tab of the NPC
 * (the verdict, « attendu », the states of a missing found noyau) stay in
 * `expected-noyau-words.ts`; what only the band says lives here.
 *
 * Nothing is derived here: the verdict, the rank, the gap and the cards come from the server
 * (`compareNoyaux`, `placeExpectedNoyau` of `shared`), and this module only words them.
 */

/** The tone a verdict wears, in the vocabulary of the status pill: the colour is never alone, the words say it. */
export const VERDICT_TONE: Readonly<
  Record<NoyauVerdict, 'success' | 'warning' | 'accent' | 'danger'>
> = {
  same: 'success',
  near: 'warning',
  mirror: 'accent',
  none: 'danger',
};

/** The three levels, in the order of the table: Pourquoi, Comment, Quoi. */
export const BAND_LEVELS: readonly { readonly level: AnalystLevel; readonly label: string }[] =
  ANALYST_LEVELS.map((level) => ({ level, label: ANALYST_LEVEL_LABELS[level] }));

/** The sign and the word of one level: « = tient » when the two noyaux carry the same letter there, « ≠ change » otherwise. */
export function levelSignWords(holds: boolean): { readonly sign: string; readonly word: string } {
  return holds ? { sign: '=', word: 'tient' } : { sign: '≠', word: 'change' };
}

/** The letter of a triplet at a level (`SGP` at `how` is `G`), « ? » where there is no noyau. */
export function levelLetter(code: NoyauCode | null, level: AnalystLevel): string {
  if (code === null) return '?';
  return code[ANALYST_LEVELS.indexOf(level)] ?? '?';
}

/**
 * Where the band says the expected noyau came from: « de Nicolas Hulot v5 » for the version the
 * replay played, « attendu actuel, Nicolas Hulot v5 : ce rejeu a été joué avant les NPC » for one
 * that played none (A7). Without a name (an NPC deleted for good) it is « du NPC v5 ».
 */
export function bandSourceWords(
  provenance: NoyauExpectedProvenance,
  npcName: string | null | undefined,
): { readonly lead: string; readonly owner: string; readonly tail: string } {
  const named = npcName === null || npcName === undefined ? 'NPC' : npcName;
  const version = `v${provenance.versionNumber}`;
  if (provenance.kind === 'current') {
    return {
      lead: 'attendu actuel, ',
      owner: `${named} ${version}`,
      tail: ' : ce rejeu a été joué avant les NPC',
    };
  }
  return {
    lead: npcName === null || npcName === undefined ? 'du ' : 'de ',
    owner: `${named} ${version}`,
    tail: '',
  };
}

/** « 1er », « 2e », « 3e »: the rank as French writes it. */
export function ordinalWords(rank: number): string {
  return rank === 1 ? '1er' : `${rank}e`;
}

/** « 3e sur 6 », or « 2e ex aequo » when another noyau has the very same note (H33). */
export function rankWords(placement: Pick<NoyauPlacement, 'rank' | 'tied'>): string {
  return placement.tied
    ? `${ordinalWords(placement.rank)} ex aequo`
    : `${ordinalWords(placement.rank)} sur ${NOYAU_CODES.length}`;
}

/** One piece of a sentence: `strong` is what the band sets in bold, the figures and the facts. */
export type BandPart = { readonly text: string; readonly strong: boolean };

const plain = (text: string): BandPart => ({ text, strong: false });
const strong = (text: string): BandPart => ({ text, strong: true });

/**
 * Where the expected noyau falls (§26.3, A4, H33, H34), in a sentence:
 *
 *   « L'attendu est 3e sur 6, à 13 points du retenu, et n'était pas aux cartes du premier choix. »
 *   « L'attendu est le retenu, à 9 points du second. »
 *
 * The gap is the note of the retained noyau minus the expected one (or, for an expected noyau that
 * IS the retained one, its note minus the best of the others): a negative gap says « au-dessus du
 * retenu » or « sous le second » rather than printing a minus sign. The cards are omitted when the
 * pair is not known (`onCards` null, H34).
 */
export function placementParts(placement: NoyauPlacement): readonly BandPart[] {
  const toSecond = placement.gapTo === 'second';
  const points = noyauPoints(Math.abs(placement.gap));
  const parts: BandPart[] = [plain('L’attendu est ')];
  if (toSecond) {
    parts.push(strong('le retenu'));
    parts.push(plain(', '));
    if (placement.gap >= 0) {
      parts.push(plain('à '), strong(points), plain(' du second'));
    } else {
      parts.push(plain('à '), strong(points), plain(' sous le second'));
    }
  } else {
    parts.push(strong(rankWords(placement)));
    parts.push(plain(', '));
    if (placement.gap >= 0) {
      parts.push(plain('à '), strong(points), plain(' du retenu'));
    } else {
      parts.push(plain('à '), strong(points), plain(' au-dessus du retenu'));
    }
  }
  if (placement.onCards !== null) {
    parts.push(
      plain(placement.onCards ? ', et était ' : ', et n’était '),
      strong(placement.onCards ? 'aux cartes' : 'pas aux cartes'),
      plain(' du premier choix'),
    );
  }
  parts.push(plain('.'));
  return parts;
}

/** The sentence of `placementParts`, as plain text. */
export function placementSentence(placement: NoyauPlacement): string {
  return placementParts(placement)
    .map((part) => part.text)
    .join('');
}

/** « SGP Souverain Magicien » - the triplet, then the deposited name. */
function codeAndName(code: NoyauCode): string {
  return `${code} ${noyauOf(code).name}`;
}

/**
 * The two noyaux of the band in full words:
 *
 *   « SGP Souverain Magicien attendu, GSP Guerrier Cosmique retenu. »
 *   « SGP Souverain Magicien attendu, aucun noyau trouvé. »
 *   « Attendu hors base, PSG Producteur Réinventeur retenu. »
 */
export function bandNamesParts(
  expected: NoyauCode | null,
  found: NoyauCode | null,
): readonly BandPart[] {
  if (expected === null) {
    return found === null
      ? [plain('Attendu hors base, aucun noyau trouvé.')]
      : [plain('Attendu hors base, '), strong(codeAndName(found)), plain(' retenu.')];
  }
  return found === null
    ? [strong(codeAndName(expected)), plain(' attendu, aucun noyau trouvé.')]
    : [
        strong(codeAndName(expected)),
        plain(' attendu, '),
        strong(codeAndName(found)),
        plain(' retenu.'),
      ];
}

/**
 * What the grey pill of a band with nothing to compare says (§26.3):
 *
 *   « Aucun noyau trouvé : sans extrait » - the determination read no extract;
 *   « En attente de la détermination » - it is not settled yet;
 *   « Aucun noyau trouvé : en échec » - it failed;
 *   « Aucun noyau trouvé » - it rendered, and there is no retained noyau (the replay stopped
 *   before the first choice);
 *   « Attendu hors base : rien à comparer » - the sheet says the NPC belongs to none of the six.
 */
export function bandNothingWords(
  expectedIsOutOfBase: boolean,
  state: NoyauDeterminationState | null,
): string {
  if (expectedIsOutOfBase) return 'Attendu hors base : rien à comparer';
  if (state === 'pending') return 'En attente de la détermination';
  const why = state === null ? null : NO_FOUND_STATE_WORDS[state];
  return why === null ? 'Aucun noyau trouvé' : `Aucun noyau trouvé : ${why}`;
}

/**
 * The line of the principal, when it differs from the retained noyau (§26.2):
 *
 *   « L'analyste lisait Producteur Réinventeur (PSG, 51) ; la carte prise dans le seuil de 5 a
 *     fait retenir GSP (46). »
 *
 * `threshold` is the one frozen at the deal of the first choice, null on a first choice dealt
 * before there was one: the sentence then says « la carte prise a fait retenir ». A note that the
 * six notes do not carry is left out of its parentheses.
 */
export function principalSentence(input: {
  readonly principal: NoyauCode;
  readonly retained: NoyauCode;
  readonly scores: readonly Pick<NoyauScore, 'noyau' | 'score'>[];
  readonly threshold: number | null;
}): string {
  const { principal, retained, scores, threshold } = input;
  const noteOf = (code: NoyauCode): number | undefined =>
    scores.find((entry) => entry.noyau === code)?.score;
  const principalNote = noteOf(principal);
  const retainedNote = noteOf(retained);
  const within = threshold === null ? '' : ` dans le seuil de ${threshold}`;
  return [
    `L’analyste lisait ${noyauOf(principal).name} (${principal}${principalNote === undefined ? '' : `, ${principalNote}`})`,
    ` ; la carte prise${within} a fait retenir ${retained}${retainedNote === undefined ? '' : ` (${retainedNote})`}.`,
  ].join('');
}
